import { useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MessageSquarePlus, Bug, Lightbulb, HelpCircle, Send, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import { logger } from "@/utils/logger";
import { toError } from "@/utils/toError";
import { useHaptic } from "@/hooks/useHaptic";

// Routes already crowded with floating UI on mobile — hide the FAB there
// to avoid masking the chat send button or the express revision controls.
const ROUTES_HIDE_FAB_MOBILE = ["/chat", "/revision-express"];

const FEEDBACK_TYPES = [
  { value: "bug", label: "Bug", icon: Bug, color: "text-destructive" },
  { value: "improvement", label: "Amélioration", icon: Lightbulb, color: "text-primary" },
  { value: "other", label: "Autre", icon: HelpCircle, color: "text-muted-foreground" },
] as const;

type FeedbackType = (typeof FEEDBACK_TYPES)[number]["value"];

export function FeedbackDrawer() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const isMobile = useIsMobile();
  const haptic = useHaptic();
  const [open, setOpen] = useState(false);
  const hideFabOnMobile =
    isMobile && ROUTES_HIDE_FAB_MOBILE.some((r) => location.pathname.startsWith(r));
  const [type, setType] = useState<FeedbackType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!user) return null;
  if (hideFabOnMobile) return null;

  const resetForm = () => {
    setType("bug");
    setTitle("");
    setDescription("");
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({ title: t("feedback.titleRequired", "Un titre est requis"), variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      // Insert feedback
      const { error: fbError } = await supabase.from("user_feedbacks" as any).insert({
        user_id: user.id,
        feedback_type: type,
        title: title.trim(),
        description: description.trim() || null,
        page_url: location.pathname,
        device_info: {
          userAgent: navigator.userAgent,
          screen: `${screen.width}x${screen.height}`,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          language: navigator.language,
        },
      } as any);

      if (fbError) throw fbError;

      // Extend trial by 7 days
      const { data: sub } = await supabase
        .from("subscribers")
        .select("trial_ends_at")
        .eq("user_id", user.id)
        .maybeSingle();

      const currentEnd = sub?.trial_ends_at ? new Date(sub.trial_ends_at).getTime() : 0;
      const newTrialEnd = new Date(
        Math.max(Date.now(), currentEnd) + 7 * 24 * 60 * 60 * 1000
      );

      await supabase
        .from("subscribers")
        .update({
          trial_ends_at: newTrialEnd.toISOString(),
          trial_expired: false,
        } as any)
        .eq("user_id", user.id);

      haptic('success');
      toast({
        title: t("feedback.thankYou", "Merci pour votre retour ! 🎉"),
        description: t("feedback.trialExtended", "+7 jours offerts sur votre essai premium"),
      });

      resetForm();
      setOpen(false);
    } catch (err) {
      logger.error("Feedback submit error:", toError(err).message);
      toast({
        title: t("feedback.error", "Erreur lors de l'envoi"),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild>
        <Button
          size="icon"
          onClick={() => haptic('light')}
          className={`fixed z-fab rounded-full shadow-lg h-12 w-12 min-h-[48px] min-w-[48px] transition-[bottom] duration-200 ${
            isMobile ? "bottom-[calc(var(--mobile-nav-safe)+1rem)] right-4" : "bottom-6 right-6"
          }`}
          aria-label={t("feedback.sendFeedback", "Envoyer un feedback")}
        >
          <MessageSquarePlus className="h-5 w-5" />
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <div className="mx-auto w-full max-w-md">
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2">
              <MessageSquarePlus className="h-5 w-5 text-primary" />
              {t("feedback.title", "Votre feedback")}
            </DrawerTitle>
            <DrawerDescription className="flex items-center gap-1.5">
              <Gift className="h-4 w-4 text-primary" />
              {t("feedback.reward", "+7 jours premium offerts pour chaque feedback !")}
            </DrawerDescription>
          </DrawerHeader>

          <div className="px-4 space-y-4">
            {/* Type selector */}
            <div className="flex gap-2">
              {FEEDBACK_TYPES.map((ft) => (
                <button
                  key={ft.value}
                  onClick={() => setType(ft.value)}
                  className={`flex-1 flex flex-col items-center gap-1.5 rounded-lg border p-3 text-xs transition-colors ${
                    type === ft.value
                      ? "border-primary bg-primary/10 font-medium"
                      : "border-border hover:bg-muted/50"
                  }`}
                >
                  <ft.icon className={`h-5 w-5 ${type === ft.value ? ft.color : "text-muted-foreground"}`} />
                  {ft.label}
                </button>
              ))}
            </div>

            {/* Title */}
            <div className="space-y-1.5">
              <Label htmlFor="fb-title">{t("feedback.titleLabel", "Titre")}</Label>
              <Input
                id="fb-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t("feedback.titlePlaceholder", "Résumez en quelques mots...")}
                maxLength={200}
              />
            </div>

            {/* Description */}
            <div className="space-y-1.5">
              <Label htmlFor="fb-desc">{t("feedback.descLabel", "Description (optionnel)")}</Label>
              <Textarea
                id="fb-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("feedback.descPlaceholder", "Décrivez le bug ou l'amélioration souhaitée...")}
                rows={3}
                maxLength={2000}
              />
            </div>
          </div>

          <DrawerFooter>
            <Button onClick={handleSubmit} disabled={submitting || !title.trim()}>
              <Send className="h-4 w-4 mr-2" />
              {submitting
                ? t("common.sending", "Envoi...")
                : t("feedback.submit", "Envoyer")}
            </Button>
            <DrawerClose asChild>
              <Button variant="outline">{t("common.cancel", "Annuler")}</Button>
            </DrawerClose>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
